import { getObjectId } from '@hero/schema';
import { useApi } from '@hero/context';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import type {
  UseOrderApi,
  UseOrderCreateMultiApi,
  UseOrderExportApi,
  UseOrderListApi,
  UseMyOrdersListApi,
  UseOrderUpdateApi,
  UseOrderLocationChangePreviewApi,
  UseOrderWaybillApi,
  UseOrderGenerateWaybillApi,
  UseCalculatePriceApi,
  UsePublishedOrdersApi,
  UseBestMatchVehiclesApi,
  UseOrderEscalateApi,
  UseOrderContractCounterApi,
  UseOrderContractCancelApi,
  UseOrderContractCompleteApi,
  UseOrderGetPublicApi,
} from './types';
import { useInfiniteQueryAPI, useFileDownloadAPI, useMutationAPI, useQueryAPI } from '../../utils';

/**
 * Get single order by id. Id can be string or Id; getObjectId is applied internally.
 * Order API uses `id` in the request. API from ApiProvider (useApi()); pass apiConfig to override apiFn, etc.
 *
 * Hook API: {@link UseOrderApi}
 */
export const useOrderApi: UseOrderApi = (id, apiConfig) => {
  const api = useApi();
  return useQueryAPI({
    entity: 'order',
    apiFn: api.orders.getById,
    // Order API expects { id } not { _id }, so we pass query directly instead of singleIdQuery
    query: id ? { id: getObjectId(id) } : undefined,
    apiConfig,
    isEnabled: () => !!id,
  });
};

/**
 * Tenant-aware unified "my orders" hook.
 *
 * Auto-scopes via `Order.tenants[]` server-side: a single endpoint
 * works for every role (carrier company / business customer /
 * individual customer / driver / broker). Callers MUST NOT pass their
 * own `carrierId` / `customerId` — those are stripped server-side.
 * Pass `clientId` to AND-narrow to "my orders involving this CRM
 * Client" (workspace / client drawer). Pass `driverId` / `vehicleId`
 * to AND-narrow to orders assigned to that Driver / Vehicle doc
 * (driver / vehicle drawer orders tabs).
 *
 * No `useApi` scope — uses the default `useApi()` so the hook works
 * in any app (carrier-dashboard / customer-dashboard / admin-panel
 * / mobile) without role-locking.
 *
 * Hook API: {@link UseMyOrdersListApi}
 */
export const useMyOrdersListApi: UseMyOrdersListApi = (query, apiConfig) => {
  const api = useApi();
  return useInfiniteQueryAPI({
    entity: 'order',
    apiFn: (api.orders as Record<string, any>).getMyOrders,
    query,
    apiConfig,
  });
};

/**
 * @deprecated Use {@link useMyOrdersListApi} for new code. This hook is
 * customer-SDK-locked and predates the tenant-aware unified endpoint.
 *
 * List orders with pagination, filters, and search. Hook manages skip per page.
 * API from ApiProvider (useApi()); pass apiConfig to override apiFn, limit, etc.
 *
 * Hook API: {@link UseOrderListApi}
 */
export const useOrderListApi: UseOrderListApi = (query, apiConfig) => {
  const api = useApi('customer');
  return useInfiniteQueryAPI({
    entity: 'order',
    apiFn: api.orders.getOrders,
    query,
    apiConfig,
  });
};

/**
 * Create multi order (customer/carrier). mutate(variables) typed from hook.
 * API from ApiProvider (useApi()); pass apiConfig to override apiFn, onSuccess, etc.
 *
 * Hook API: {@link UseOrderCreateMultiApi}
 */
export const useOrderCreateMultiApi: UseOrderCreateMultiApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'order',
    apiFn: api.orders.createMultiOrder,
    apiConfig,
  });
};

/**
 * Update order (POD, cancel, publish, etc.). mutate(variables) typed from hook.
 * API from ApiProvider (useApi()); works in any app without overrides.
 *
 * Hook API: {@link UseOrderUpdateApi}
 *
 * **Prefer the focused wrappers** below for typical UIs:
 * - {@link useUpdateOrderPriceApi} — leg amounts + fees only
 * - {@link useAssignOrderApi}      — carrier/driver/vehicle in one PATCH
 * - {@link useChangeOrderStatusApi} — status transitions (book / confirm / accept / pickup / deliver)
 * - {@link useCancelOrderApi}      — cancellations with structured reason
 *
 * The split is documented in `docs/relations-v2/12-order-actions-state-machine.md`.
 */
export const useOrderUpdateApi: UseOrderUpdateApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'order',
    apiFn: api.orders.updateOrder,
    apiConfig,
  });
};

/**
 * Preview what a destination edit would do — city change, guards, and the
 * price the new lane quotes — before committing it.
 *
 * `defaultInvalidationStrategy: 'none'` because it writes nothing;
 * invalidating the order cache on a preview would refetch for no reason.
 *
 * Hook API: {@link UseOrderLocationChangePreviewApi}
 */
export const useOrderLocationChangePreviewApi: UseOrderLocationChangePreviewApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'order',
    apiFn: api.orders.previewOrderLocationChange,
    defaultInvalidationStrategy: 'none',
    apiConfig,
  });
};

/**
 * Phase 6 — focused hook for editing the per-leg amount + fees on an
 * order. Wraps `useOrderUpdateApi` so the call surface is always
 * `{ _id, beneficiaryAmount?, carrierAmount? }` and consumers can't
 * accidentally bundle other order updates with the same patch (the
 * "messy" multi-intent updates were one of the reasons we split this
 * out — see Phase 6 of `order_pipeline_rewrite_19838b81.plan.md`).
 */
export const useUpdateOrderPriceApi: UseOrderUpdateApi = apiConfig =>
  useOrderUpdateApi(apiConfig);

/**
 * Phase 6 — focused hook for the manual assign drawer. The variables
 * shape is identical to `useOrderUpdateApi` but the recommended call
 * is `mutate({ _id, carrier?, driver?, vehicle? })` — the backend's
 * `AssignCarrierDriverHandler` (Phase 3) accepts all three in a
 * single transactional write so the carrier-app + admin no longer
 * need the legacy two-step PATCH dance.
 */
export const useAssignOrderApi: UseOrderUpdateApi = apiConfig =>
  useOrderUpdateApi(apiConfig);

/**
 * Phase 6 — focused hook for status transitions. Recommended call
 * shape `mutate({ _id, action, messages? })` where `action` is one
 * of the {@link OrderActionTypes} values (`accept_order`,
 * `confirm_order`, `book_order`, `pickup_order`, `arrived_pickup`, …).
 */
export const useChangeOrderStatusApi: UseOrderUpdateApi = apiConfig =>
  useOrderUpdateApi(apiConfig);

/**
 * Phase 6 — focused hook for the cancellation flow. Recommended call
 * shape `mutate({ _id, action: 'cancel_order' | ..., cancellation })`.
 * Built on top of `useOrderUpdateApi` so it shares the same cache
 * invalidation rules as the rest of the order updates.
 */
export const useCancelOrderApi: UseOrderUpdateApi = apiConfig =>
  useOrderUpdateApi(apiConfig);

/**
 * Get order waybill by order id. Id can be string or Id; getObjectId is applied internally.
 * API from ApiProvider (useApi()); pass apiConfig to override apiFn, etc.
 *
 * Hook API: {@link UseOrderWaybillApi}
 */
export const useOrderWaybillApi: UseOrderWaybillApi = (id, apiConfig) => {
  const api = useApi();
  return useQueryAPI({
    entity: 'waybill',
    apiFn: api.orders.getWaybill,
    // Waybill API expects { id } not { _id }, so we pass query directly instead of singleIdQuery
    query: id ? { id: getObjectId(id) } : undefined,
    apiConfig,
    isEnabled: () => !!id,
  });
};

/**
 * Request async waybill generation. Marks the order's waybill as
 * `generating` and returns immediately — PDF work continues on the
 * server. Invalidates `order` queries so the drawer can poll status.
 *
 * Hook API: {@link UseOrderGenerateWaybillApi}
 */
export const useOrderGenerateWaybillApi: UseOrderGenerateWaybillApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'order',
    apiFn: api.orders.generateWaybill,
    apiConfig,
  });
};

/**
 * Export orders to Excel. Handles blob download and triggers file save.
 * Default toast on success/error using m-order translations.
 * API from ApiProvider (useApi()); consumer can override filename, onSuccess, onError.
 *
 * Hook API: {@link UseOrderExportApi}
 */
export const useOrderExportApi: UseOrderExportApi = apiConfig => {
  const api = useApi();
  const { t } = useTranslation(['m-order']);
  const exportFn = (api.orders as Record<string, any>).exportOrders;
  return useFileDownloadAPI({
    entity: 'order',
    apiFn: exportFn,
    apiConfig,
    defaultFilename: `orders-${Date.now()}.xlsx`,
    defaultOnSuccess: () => toast.success(t('m-order:toast.success.orders_exported')),
    defaultOnError: (error: Error) =>
      toast.error(t('m-order:toast.error.orders_export', { message: error.message })),
  });
};

/**
 * Calculate price for multiple routes. Default invalidation: none.
 * API from ApiProvider (useApi()); pass apiConfig to override apiFn, etc.
 *
 * Hook API: {@link UseCalculatePriceApi}
 */
export const useCalculatePriceApi: UseCalculatePriceApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'order',
    apiFn: api.price.calcMultiRoutesPrice,
    apiConfig,
    defaultInvalidationStrategy: 'none',
  });
};

// --- Order lists ---
//
// There is exactly one list hook for orders: `useMyOrdersListApi`
// (defined above). It hits the tenant-aware `GET /order/my-orders`
// endpoint and accepts the full `MyOrdersAPI['req']` filter surface —
// most importantly `unifiedStatus` (active / delivered / canceled /
// pending / published / …) and the optional `lens`
// (`'as_carrier' | 'as_customer' | 'as_shipper' | 'as_driver' |
// 'none'`). The previous per-role wrappers
// (`useActiveOrdersCarrierApi`, `useActiveOrdersCustomerApi`, and the
// single-item `useActiveDriverOrderApi`) were just filter aliases
// over the same endpoint and have been removed — call
// `useMyOrdersListApi` directly with the appropriate
// `unifiedStatus` (and `lens` when role-narrowing is meaningful).

/**
 * Published orders (jobs) available for carriers to book.
 * API from ApiProvider (useApi('carrier')); pass apiConfig to override apiFn, limit, etc.
 *
 * Hook API: {@link UsePublishedOrdersApi}
 */
export const usePublishedOrdersApi: UsePublishedOrdersApi = (query, apiConfig) => {
  const api = useApi('carrier');
  return useInfiniteQueryAPI({
    entity: 'publishedOrder',
    apiFn: api.orders.getPublishedOrders,
    query,
    apiConfig,
  });
};

/**
 * Best match vehicles for a given order.
 * API from ApiProvider (useApi('carrier')); pass apiConfig to override apiFn, limit, etc.
 *
 * Hook API: {@link UseBestMatchVehiclesApi}
 */
export const useBestMatchVehiclesApi: UseBestMatchVehiclesApi = (query, apiConfig) => {
  const api = useApi('carrier');
  return useInfiniteQueryAPI({
    entity: 'vehicle',
    apiFn: api.orders.getBestMatchVehicles,
    query,
    apiConfig,
  });
};

/**
 * Manually escalate the allocation cascade for the caller's order. When the
 * caller is `Order.shipper` and `promoteToPublic=true`, broadcasts to the public
 * load board; otherwise forces `private_network` (server-enforced).
 */
export const useOrderEscalateApi: UseOrderEscalateApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'order',
    apiFn: (api.orders as Record<string, any>).escalate,
    apiConfig,
  });
};

/** Phase 2 — counter-propose on a pushed contract leg. */
export const useOrderContractCounterApi: UseOrderContractCounterApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'order',
    apiFn: (api.orders as Record<string, any>).counterContract,
    apiConfig,
  });
};

/** Phase 2 — cancel a pushed contract leg. */
export const useOrderContractCancelApi: UseOrderContractCancelApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'order',
    apiFn: (api.orders as Record<string, any>).cancelContract,
    apiConfig,
  });
};

/** Phase 2 — mark a contract leg complete. */
export const useOrderContractCompleteApi: UseOrderContractCompleteApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'order',
    apiFn: (api.orders as Record<string, any>).completeContract,
    apiConfig,
  });
};

/**
 * Unauthenticated public order lookup for share links (`/o/[ref]`, `/d/[ref]`).
 * Pass `{ ref }` or `{ id }`. No auth required — uses `useApi()` + CoreSDK.
 *
 * Hook API: {@link UseOrderGetPublicApi}
 */
export const useOrderGetPublicApi: UseOrderGetPublicApi = (query, apiConfig) => {
  const api = useApi();
  return useQueryAPI({
    entity: 'order',
    apiFn: api.orders.getPublic,
    query,
    apiConfig,
    isEnabled: () => !!(query?.ref || query?.id),
  });
};
